import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useAuth as useClerkAuth, useUser, useClerk } from '@clerk/react';
import api from '../services/api';
import billingService from '../services/billingService';

const AuthContext = createContext(null);

const HAS_CLERK = Boolean(process.env.REACT_APP_CLERK_PUBLISHABLE_KEY);

const getStoredTokens = () => ({
  accessToken: localStorage.getItem('access_token'),
  refreshToken: localStorage.getItem('refresh_token'),
  userId: localStorage.getItem('user_id'),
  sessionId: localStorage.getItem('session_id'),
});

const setStoredTokens = ({ access_token, refresh_token, user_id, session_id }) => {
  localStorage.setItem('access_token', access_token);
  localStorage.setItem('refresh_token', refresh_token);
  localStorage.setItem('user_id', user_id);
  if (session_id) {
    localStorage.setItem('session_id', session_id);
  }
};

const clearStoredTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_id');
  localStorage.removeItem('session_id');
};

/* ── Clerk-backed provider ─────────────────────────────────────────────
   Clerk owns the identity; on sign-in we exchange the Clerk session token
   with the backend (/api/auth/clerk) for the app's own JWT, so every
   downstream API call (usage, profile, billing) is unchanged. */
const ClerkAuthProvider = ({ children }) => {
  const { isLoaded, isSignedIn, getToken, signOut: clerkSignOut } = useClerkAuth();
  const { user } = useUser();
  const clerk = useClerk();

  const [sessionUser, setSessionUser] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [usageStatus, setUsageStatus] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [billingStatus, setBillingStatus] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  const sessionSyncedRef = useRef(false);
  // Guards the sync effect so it runs at most once per signed-in user, instead
  // of re-firing on every render (Clerk's getToken identity changes between
  // renders, which otherwise caused an infinite /api/auth/clerk loop).
  const syncedForUserRef = useRef(null);

  const exchangeClerkToken = useCallback(async () => {
    const token = await getToken();
    if (!token) throw new Error('No Clerk session token available');
    const response = await api.post('/api/auth/clerk', { token });
    setStoredTokens({
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      user_id: response.data.user_id,
      session_id: response.data.session_id,
    });
    localStorage.setItem('duckpilot_mode', 'auth');
    const me = await api.get('/api/auth/me');
    setSessionUser(me.data);
    sessionSyncedRef.current = true;
    return me.data;
  }, [getToken]);

  const fetchProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const response = await api.get('/api/profile');
      setProfile(response.data);
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch profile:', error);
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, []);

  const fetchBillingStatus = useCallback(async () => {
    setBillingLoading(true);
    try {
      const response = await billingService.fetchBillingStatus();
      setBillingStatus(response);
      return response;
    } catch (error) {
      console.warn('Failed to fetch billing status:', error);
      return null;
    } finally {
      setBillingLoading(false);
    }
  }, []);

  const confirmCheckoutSession = useCallback(async (sessionId) => {
    setBillingLoading(true);
    try {
      const response = await billingService.confirmCheckoutSession(sessionId);
      setBillingStatus(response);
      return response;
    } catch (error) {
      console.warn('Failed to confirm checkout session:', error);
      return null;
    } finally {
      setBillingLoading(false);
    }
  }, []);

  const refreshUsage = useCallback(async () => {
    try {
      const response = await api.get('/api/usage/status');
      setUsageStatus(response.data);
      return response.data;
    } catch (error) {
      console.warn('Failed to refresh usage status:', error);
      return null;
    }
  }, []);

  const fetchSessionUser = useCallback(async () => {
    try {
      const response = await api.get('/api/auth/me');
      setSessionUser(response.data);
      return response.data;
    } catch (error) {
      console.warn('Failed to refresh session user:', error);
      return null;
    }
  }, []);

  const syncSession = useCallback(async () => {
    setSessionError(null);
    await exchangeClerkToken();
    await refreshUsage();
    await fetchProfile();
    await fetchBillingStatus();
  }, [exchangeClerkToken, refreshUsage, fetchProfile, fetchBillingStatus]);

  // React to Clerk auth state. Depends ONLY on stable primitives (isLoaded,
  // isSignedIn, the user id) — never on callback identities — and gates on
  // syncedForUserRef so the backend exchange runs exactly once per user.
  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      syncedForUserRef.current = null;
      sessionSyncedRef.current = false;
      setSessionUser(null);
      setUsageStatus(null);
      setProfile(null);
      setBillingStatus(null);
      clearStoredTokens();
      localStorage.removeItem('duckpilot_mode');
      return;
    }

    const uid = user?.id || 'signed-in';
    if (syncedForUserRef.current === uid) return; // already attempted for this user
    syncedForUserRef.current = uid;

    let active = true;
    (async () => {
      setSessionLoading(true);
      try {
        const tokens = getStoredTokens();
        if (tokens.accessToken && sessionSyncedRef.current) {
          await refreshUsage();
          await fetchProfile();
          await fetchBillingStatus();
        } else {
          await syncSession();
        }
      } catch (error) {
        console.error('Failed to sync session with backend:', error);
        // Do NOT reset syncedForUserRef here — a failed exchange must not
        // retry in a tight loop. The error is surfaced via sessionError and
        // the user can reload to retry.
        if (active) setSessionError(error);
        sessionSyncedRef.current = false;
      } finally {
        if (active) setSessionLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded, isSignedIn, user?.id]);

  const openSignIn = useCallback(() => clerk.openSignIn(), [clerk]);
  const openSignUp = useCallback(() => clerk.openSignUp(), [clerk]);

  const signOut = useCallback(async () => {
    clearStoredTokens();
    localStorage.removeItem('duckpilot_mode');
    setSessionUser(null);
    setUsageStatus(null);
    setProfile(null);
    setBillingStatus(null);
    sessionSyncedRef.current = false;
    await clerkSignOut();
  }, [clerkSignOut]);

  const value = useMemo(() => ({
    // Back-compat: `firebaseUser` is consumed by DashboardPage redirect logic.
    firebaseUser: isSignedIn ? user : null,
    user,
    sessionUser,
    usageStatus,
    profile,
    profileLoading,
    billingStatus,
    billingLoading,
    authLoading: !isLoaded,
    sessionLoading,
    sessionError,
    isSignedIn: Boolean(isSignedIn),
    isAuthenticated: Boolean(isSignedIn) && Boolean(sessionUser),
    refreshUsage,
    fetchSessionUser,
    fetchProfile,
    fetchBillingStatus,
    confirmCheckoutSession,
    syncSession,
    openSignIn,
    openSignUp,
    // Legacy method names kept so existing callers don't break — all route to Clerk's UI.
    signInWithGoogle: openSignIn,
    signInWithEmail: openSignIn,
    signUpWithEmail: openSignUp,
    signOut,
  }), [isSignedIn, user, sessionUser, usageStatus, profile, profileLoading, billingStatus, billingLoading, isLoaded, sessionLoading, sessionError, refreshUsage, fetchSessionUser, fetchProfile, fetchBillingStatus, confirmCheckoutSession, syncSession, openSignIn, openSignUp, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/* ── Fallback provider (no Clerk key) — demo-only, never crashes ──────── */
const FallbackAuthProvider = ({ children }) => {
  const noop = useCallback(() => {}, []);
  const asyncNoop = useCallback(async () => null, []);
  const value = useMemo(() => ({
    firebaseUser: null,
    user: null,
    sessionUser: null,
    usageStatus: null,
    profile: null,
    profileLoading: false,
    billingStatus: null,
    billingLoading: false,
    authLoading: false,
    sessionLoading: false,
    sessionError: null,
    isSignedIn: false,
    isAuthenticated: false,
    refreshUsage: asyncNoop,
    fetchSessionUser: asyncNoop,
    fetchProfile: asyncNoop,
    fetchBillingStatus: asyncNoop,
    confirmCheckoutSession: asyncNoop,
    syncSession: asyncNoop,
    openSignIn: noop,
    openSignUp: noop,
    signInWithGoogle: noop,
    signInWithEmail: noop,
    signUpWithEmail: noop,
    signOut: asyncNoop,
  }), [noop, asyncNoop]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const AuthProvider = ({ children }) => (
  HAS_CLERK
    ? <ClerkAuthProvider>{children}</ClerkAuthProvider>
    : <FallbackAuthProvider>{children}</FallbackAuthProvider>
);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};
